import classNames from 'classnames'
import _ from 'lodash'
import moment from 'moment'
import { Toast, Loader, Tip } from '@vzmi/shp_shared_compo'
import { getFeatureToggle } from '@vzmi/component_ec_nodejs_util/dist/lib/FeatureToggleUtil'
import { connect } from 'react-redux'
import styles from './SalesPerformance.css'

import {
  BESTSELLER_FILTER_TYPE_MAP,
  DEFAULT_NO_FOUND_DESCRIPTION
} from '../../../../constants/globalTypes'
import safeAwait from '../../../../../lib/safeAwait'
import SPBaseModule from '../../../../lib/SPBaseModule'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { numeralFormat } from '../../../../../lib/renderUtil'
import Block from '../../../Common/NewDashboard/Block/Block'
import Tabs from '../../../Common/NewDashboard/Tabs/Tabs'
import Divider from '../../../Common/NewDashboard/Divider/Divider'
import SummaryBanner from '../../../Common/NewDashboard/Chart/SummaryBanner/SummaryBanner'
import Sort from '../../../Common/NewDashboard/Sort/Sort'
import PartNotFound from '../../../Common/NewDashboard/PartNotFound/PartNotFound'
import NoData from '../../../Common/NoData/NoData'
import SearchForm, {
  FIELD as SEARCH_FIELD,
  INIT_VALUES as SEARCH_FORM_INIT_VALUES,
  genStartDateTimeOptions
} from './SearchForm/SearchForm'
import PerformanceChart, {
  FIELD as CHART_FIELD
} from './PerformanceChart/PerformanceChart'
import RankingTable, {
  FIELD as RANKING_TABLE_FIELD
} from './RankingTable/RankingTable'
import { LISTING_TYPE } from '../constants/listing'
import {
  GA_CLICK_ACTION,
  getI13nModel
} from './SalesPerformance.ga'
import { When } from '../../../Common/Form'
import { getRangeAPIDateTimes } from '../../../../lib/MomentHelper'

export const PERF_INIT_DATA = {
  salesSummaries: [],
  performances: [],
  sellersRankings: []
}

const LISTING_ID_NO_FOUND_DESCRIPTION = '查無編號，請重新確認'

const SUMMARY_MAP = {
  [CHART_FIELD.AMOUNT]: '訂購總金額',
  [CHART_FIELD.QUANTITY]: '訂購總數量'
}

export const TAB = {
  PERF_CHART: 'perf_chart',
  RANKING_TABLE: 'ranking_table'
}

const TAB_MAP = {
  [TAB.PERF_CHART]: '訂購金額／數量',
  [TAB.RANKING_TABLE]: '賣場排行'
}

export const SORT_OPTIONS = [
  {
    value: BESTSELLER_FILTER_TYPE_MAP.AMOUNT.key,
    text: BESTSELLER_FILTER_TYPE_MAP.AMOUNT.text
  },
  {
    value: BESTSELLER_FILTER_TYPE_MAP.QUANTITY.key,
    text: BESTSELLER_FILTER_TYPE_MAP.QUANTITY.text
  }
]

export const RANKING_SIZE = 5

export const transformValuesToPerformancesParams = (formValues) => {
  const payload = {}

  for (const field of _.keys(formValues)) {
    const fieldValue = formValues[field]
    switch (field) {
      case SEARCH_FIELD.LISTING_IDS: {
        if (formValues[SEARCH_FIELD.LISTING_TYPE] === LISTING_TYPE.ORDER) {
          const idWithoutEndComma = _.replace(fieldValue, /\D$/, '')
          _.set(payload, field, idWithoutEndComma)
        }
        break
      }
      case 'reportDate':
        _.set(payload, 'reportDate', fieldValue)
        break
      default: {
        break
      }
    }
  }

  return payload
}

export const transformValuesToBestSellersParams = (formValues, state) => {
  let payload = {}

  for (const field of _.keys(formValues)) {
    const fieldValue = formValues[field]
    switch (field) {
      case SEARCH_FIELD.LISTING_TYPE: {
        // 查詢全部賣場只取前 5 筆
        if (fieldValue === LISTING_TYPE.ALL) {
          _.set(payload, 'limit', RANKING_SIZE)
        }
        break
      }
      case SEARCH_FIELD.LISTING_IDS: {
        if (formValues[SEARCH_FIELD.LISTING_TYPE] === LISTING_TYPE.ORDER) {
          const idWithoutEndComma = _.replace(fieldValue, /\D$/, '')
          _.set(payload, field, idWithoutEndComma)
        }
        break
      }
      case 'reportDate':
        _.set(payload, 'reportDate', fieldValue)
        break
      default: {
        break
      }
    }
  }
  payload = {
    ...payload,
    // 和 performance 取 24 小時(一天)
    term: 1,
    realTime: true,
    sort: `-${state.rankingSortType}`
  }

  return payload
}

export const transformSummary = summary => {
  const salesSummaries = [
    {
      label: SUMMARY_MAP[CHART_FIELD.AMOUNT],
      value: `$${numeralFormat(
        _.toNumber(_.get(summary, CHART_FIELD.AMOUNT, 0)),
        { allowZero: true }
      )}`
    },
    {
      label: SUMMARY_MAP[CHART_FIELD.QUANTITY],
      value: numeralFormat(
        _.toNumber(_.get(summary, CHART_FIELD.QUANTITY, 0)),
        { allowZero: true }
      )
    }
  ]
  return salesSummaries
}

export const transformSellersRankings = (sellersRankings, rankingSize) => {
  const convertedSellersRankings = _.flow(
    valuableData => _.map(valuableData, (item, index) => {
      item.ranking = index + 1
      item.soldAmount = +item.soldAmount || 0
      item.soldQuantity = +item.soldQuantity
      item.availableQuantity = +item.availableQuantity || 0
      return item
    }),
    pureData => _.slice(pureData, 0, rankingSize)
  )(sellersRankings)
  return convertedSellersRankings
}

export const getNotFoundListingIds = (formValues, rankingData) => {
  const isNeedNotFoundIds = (
    formValues[SEARCH_FIELD.LISTING_TYPE] === LISTING_TYPE.ORDER
  )
  let notFoundListingIds = isNeedNotFoundIds
    ? _.xor(
      _.compact(
        _.split(formValues[SEARCH_FIELD.LISTING_IDS], COMMA)
      ),
      _.map(rankingData, item => item[RANKING_TABLE_FIELD.LISTING_ID])
    )
    : []
  notFoundListingIds = _.join(notFoundListingIds, COMMA)
  return notFoundListingIds
}

export const checkIsNoOrderListingIdFound = (formValues, sellersRankings) => {
  let isNoOrderListingIdFound = false
  const listingType = formValues[SEARCH_FIELD.LISTING_TYPE]

  switch (listingType) {
    case LISTING_TYPE.ORDER:
      isNoOrderListingIdFound = _.isEmpty(sellersRankings)
      break
    // 沒有指定賣場沒有查不到指定賣場的問題
    case LISTING_TYPE.ALL:
      isNoOrderListingIdFound = false
      break
    default:
      isNoOrderListingIdFound = false
      break
  }
  return isNoOrderListingIdFound
}

export const checkIsNoPerformanceFound = summary => {
  const {
    amount,
    quantity
  } = summary
  const isNoPerformanceFound = (
    _.toNumber(amount) === 0 &&
    quantity === 0
  )
  return isNoPerformanceFound
}

class SalesPerformance extends SPBaseModule {
  constructor (props) {
    super(props)
    this.state = {
      datetimeRange: [],
      startDateTimeOptions: [],
      researchedFormData: {},
      rankingSortType: BESTSELLER_FILTER_TYPE_MAP.AMOUNT.key,
      selectedTab: TAB.PERF_CHART,
      rankingColumnProps: {
        rankingSize: RANKING_SIZE,
        width: 35
      },
      notFoundListingIds: '',
      isNoOrderListingIdFound: false,
      isNoPerformanceFound: false,
      ...PERF_INIT_DATA
    }
    this.searchFormProps = null
  }

  componentDidMount () {
    // 在 redux form 外觸發 submit
    this.searchFormProps.handleSubmit(this.onSearch)()
  }

  setSearchFormProps = formProps => (this.searchFormProps = formProps)

  onSearch = values => this.loading(async () => {
    const { selectedTab } = values
    const { startDateTimeOptions, datetimeRange: [, endDateTime] } = this.state
    const searchStartDatetime = startDateTimeOptions.length > 0 && _.get(values[SEARCH_FIELD.START_DATETIME], '0')
    const startDateTimeMoment =
      searchStartDatetime &&
      startDateTimeOptions.find(el => el.label === searchStartDatetime).moment
    const reportDate = startDateTimeMoment
      ? getRangeAPIDateTimes(startDateTimeMoment, moment(endDateTime))
      : ''
    if (reportDate) {
      values.reportDate = reportDate
    }
    const currentRankingSortType = BESTSELLER_FILTER_TYPE_MAP.AMOUNT.key
    this.setState({
      researchedFormData: values,
      notFoundListingIds: '',
      rankingSortType: currentRankingSortType
    })

    const updateStatusList = await Promise.all([
      this.updatePerformanceChart(values),
      this.updateRankingTable(values, { rankingSortType: currentRankingSortType })
    ])
    const [errorMessage] = _.find(updateStatusList, ([error]) => error) || []
    if (errorMessage) {
      Toast.error(errorMessage)
      this.setIsLoading(false)
      return
    }
    this.setState({
      // 預設會自動跳回圖表頁籤
      selectedTab: selectedTab || TAB.PERF_CHART
    })
    this.setIsSuccess(true)
  })

  updatePerformanceChart = async (values) => {
    const { fetchSalesPerformances } = this.props
    const params = transformValuesToPerformancesParams(values)
    const [error, payload] = await safeAwait(
      fetchSalesPerformances(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    const {
      statistics: performances = [],
      summary = {},
      reportDateStart,
      reportDateEnd
    } = payload
    const isReportDateExist = (reportDateStart && reportDateEnd)
    const datetimeRange = isReportDateExist ? [reportDateStart, reportDateEnd] : []
    const startDateTimeOptions = isReportDateExist ? genStartDateTimeOptions(reportDateEnd) : []
    const salesSummaries = transformSummary(summary)
    const isNoPerformanceFound = checkIsNoPerformanceFound(summary)
    const convertedPreformances = _.map(performances, perf => ({ ...perf, amount: +perf.amount }))
    this.setState({
      datetimeRange,
      startDateTimeOptions,
      salesSummaries,
      performances: convertedPreformances,
      isNoPerformanceFound
    })
    return [undefined, payload]
  }

  updateRankingTable = async (values, state) => {
    const { fetchPaginationBestsellers } = this.props
    const params = transformValuesToBestSellersParams(values, state)
    const [error, payload] = await safeAwait(
      fetchPaginationBestsellers(params).then(httpHandler)
    )
    const errorMessage = errorHelper.parseErrorMessage(error)
    if (errorMessage) {
      return [errorMessage]
    }

    const { bestsellers: sellersRankings = [] } = payload
    const convertedSellersRankings = transformSellersRankings(sellersRankings, RANKING_SIZE)
    const notFoundListingIds = getNotFoundListingIds(values, convertedSellersRankings)
    const isNoOrderListingIdFound = checkIsNoOrderListingIdFound(values, sellersRankings)
    this.setState({
      sellersRankings: convertedSellersRankings,
      notFoundListingIds,
      isNoOrderListingIdFound
    })
    return [undefined, payload]
  }

  onTabChange = tabKey => {
    this.setState({ selectedTab: tabKey })
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TAB,
      payload: tabKey
    })
  }

  onListingTypeChange = async (e, value) => {
    const isChangeSortTypeTriggerSearch = (value === LISTING_TYPE.ALL)
    this.searchFormProps.untouch(SEARCH_FIELD.LISTING_IDS)
    if (isChangeSortTypeTriggerSearch) {
      this.onSearch(SEARCH_FORM_INIT_VALUES)
    }
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.LISTING_OPTION,
      payload: value
    })
  }

  onRankingTableSortChange = (event) => {
    const previousValue = this.state.rankingSortType
    const newValue = _.get(event, 'target.value', '')
    if (newValue === previousValue) {
      return
    }

    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TABLE_SORT,
      payload: newValue
    })
    const newState = { rankingSortType: newValue }
    this.setState(newState)
    this.loading(async () => {
      const { researchedFormData } = this.state
      const [error] = await this.updateRankingTable(researchedFormData, newState)
      if (error) {
        Toast.error(error)
        this.setIsLoading(false)
        return
      }
      this.setIsSuccess(true)
    })
  }

  sendPageviewByAction = (action) => {
    const i13nModel = getI13nModel(action)
    this.props.sendDashboardPageview(i13nModel)
  }

  sendTableLinkPageview = payload => {
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.TABLE_LINK,
      payload
    })
  }

  onReadmoreClick = e => {
    this.sendPageviewByAction({
      type: GA_CLICK_ACTION.READ_MORE
    })
  }

  render () {
    const {
      className,
      sharedLink,
      device,
      flags
    } = this.props
    const {
      selectedTab,
      datetimeRange,
      rankingSortType,
      salesSummaries,
      performances,
      sellersRankings,
      rankingColumnProps,
      notFoundListingIds,
      isNoOrderListingIdFound,
      isNoPerformanceFound,
      isLoading,
      startDateTimeOptions,
      researchedFormData: { listingIds, listingType }
    } = this.state
    // 指定賣場有"部分"賣場沒有查到時為 true
    const isPartListingIdNoFoundVisible = (
      !isNoOrderListingIdFound && !_.isEmpty(notFoundListingIds)
    )
    // 圖表查無資料提示文字
    const chartNoFoundDescription = isNoPerformanceFound
      ? isNoOrderListingIdFound
        ? LISTING_ID_NO_FOUND_DESCRIPTION
        : DEFAULT_NO_FOUND_DESCRIPTION
      : ''
    // 表格查無資料提示文字
    const tableNoFoundDescription = isNoOrderListingIdFound
      ? LISTING_ID_NO_FOUND_DESCRIPTION
      : DEFAULT_NO_FOUND_DESCRIPTION
    const clz = classNames(styles.SalesPerformance, {
      [className]: !!className
    })
    const gdidParam = listingIds && listingType === LISTING_TYPE.ORDER
      ? `?gdids=${listingIds}`
      : ''
    return (
      <div
        className={clz}
        data-role='sales-performance'
      >
        <When
          value={device.isMobile && getFeatureToggle(flags, 'sp_mobile')}
          isTrue
        >
          <MoveToMobileLink />
        </When>
        <Loader loaded={!isLoading}>
          <Block noMarginTop>
            <SearchForm
              datetimeRange={datetimeRange}
              onListingTypeChange={this.onListingTypeChange}
              onFormInit={this.setSearchFormProps}
              onSubmit={this.onSearch}
              startDateTimeOptions={startDateTimeOptions}
            />
          </Block>
          {
            isPartListingIdNoFoundVisible && (
              <PartNotFound>
                查無下列賣場編號，如需查詢，請重新確認：
                {notFoundListingIds}
              </PartNotFound>
            )}
          <Tabs
            activeKey={selectedTab}
            onChange={this.onTabChange}
            items={[
              {
                label: TAB_MAP[TAB.PERF_CHART],
                key: TAB.PERF_CHART,
                children: (
                  <Block noMarginBottom>
                    {
                      isNoPerformanceFound
                        ? (
                          <NoData
                            className={styles.chartNoData}
                            description={chartNoFoundDescription}
                          />
                        )
                        : (
                          <>
                            <SummaryBanner summaries={salesSummaries} />
                            <PerformanceChart data={performances} />
                            <Divider />
                            <Tip type='info'>以時為單位，統計過去24小時業績</Tip>
                          </>
                        )
                    }
                  </Block>
                )
              },
              {
                label: TAB_MAP[TAB.RANKING_TABLE],
                key: TAB.RANKING_TABLE,
                children: (
                  <>
                    <Sort
                      id='sales-performance'
                      options={SORT_OPTIONS}
                      onChange={this.onRankingTableSortChange}
                      sortType={rankingSortType}
                    />
                    <RankingTable
                      sharedLink={sharedLink}
                      data={sellersRankings}
                      rankingColumnProps={rankingColumnProps}
                      noFoundDescription={tableNoFoundDescription}
                      sendTableLinkPageview={this.sendTableLinkPageview}
                      readmoreProps={{
                        href: `/item/salesPerformance${gdidParam}`,
                        onClick: this.onReadmoreClick
                      }}
                    />
                    <Tip
                      className={styles.rankingTableTip}
                      type='info'
                    >
                      賣場排行，顯示至前5名
                    </Tip>
                  </>
                )
              }
            ]}
          />
        </Loader>
      </div>
    )
  }
}

const MoveToMobileLink = () => (
  <a
    href="/m/dashboard?mobile=1"
    className={styles.moveToMobileLink}
  >
    使用手機版查看「即時業績」
  </a>
)

const mapStateToProps = state => ({
  device: state.device,
  flags: state.flags
})

export default connect(mapStateToProps)(SalesPerformance)
